import React, { Component } from 'react'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import classnames from 'classnames'
import { get, uniqueId, pick, omit, without, findLast, dropRightWhile } from 'lodash'

import '../styles/mount.scss'

const TRANSITION_KEYS = ['classNames', 'appear', 'enter', 'exit', 'timeout']

const DEFAULT_TRANSITION = {
  enter: true,
  exit: true,
  classNames: {
    enter: 'modal-enter',
    enterActive: 'modal-enter-active',
    exit: 'modal-exit',
    exitActive: 'modal-exit-active'
  },
  timeout: {
    enter: 250,
    exit: 250
  }
}

export const MountContext = React.createContext({})

export default class Mount extends Component {
  static defaultProps = {
    onMountChange: () => {}
  }

  constructor(props) {
    super(props)

    this.state = {
      stack: [],
      mounted: {},
      transitionOptions: DEFAULT_TRANSITION
    }

    this._mobile = React.createRef()
  }

  componentDidUpdate = (prevProps, prevState) => {
    if (this.state.stack !== prevState.stack) {
      this.props.onMountChange(this.state.stack)
    }
  }

  render() {
    const isMounted = this.state.mounted[get(this.state, 'stack[0].key', null)] || false
    return (
      <MountContext.Provider value={{ push: this.push, pop: this.pop }}>
        <div className="account-only-mobile" ref={this._mobile} />
        <div className={classnames('account-mount__content', { 'is-mounted': isMounted })}>{this.props.children}</div>
        <TransitionGroup style={{ height: isMounted ? '100%' : 'auto' }}>
          {this.state.stack.map(this._renderStackItem)}
        </TransitionGroup>
      </MountContext.Provider>
    )
  }

  _renderStackItem = (item, index) => {
    const itemIndex = this.state.stack.indexOf(item)
    const isMounted = this.state.mounted[get(this.state, `stack[${itemIndex + 1}].key`, null)] || false
    return (
      <CSSTransition key={item.key} {...this.state.transitionOptions}>
        <div className={classnames('account-mount__layer', { 'is-mounted': isMounted })}>{item.component}</div>
      </CSSTransition>
    )
  }

  push = (component, options = {}) => {
    return new Promise((resolve, reject) => {
      const componentNode = React.createElement(component, options.props)
      const transitionOptions = { ...DEFAULT_TRANSITION, ...pick(options, TRANSITION_KEYS) }

      const stack = this.state.stack.concat({
        autoclose: true,
        ...options,
        resolve,
        reject,
        scrollTop: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
        component: componentNode,
        key: uniqueId('mount-component-')
      })

      this.setState({ transitionOptions }, () => {
        this.setState({ stack })

        if (transitionOptions.timeout && transitionOptions.timeout.enter) {
          this._mountTimeout = setTimeout(this._didMount, transitionOptions.timeout.enter)
        } else {
          this._didMount(stack)
        }
      })
    })
  }

  autoclose = () => {
    if (this.state.stack.length === 0) {
      return
    }

    let nextStack = dropRightWhile(this.state.stack, 'autoclose', true)
    if (nextStack.length === this.state.stack.length) {
      return
    }

    const resolvedItems = without(this.state.stack, nextStack)
    const nextMounted = omit(
      this.state.mounted,
      resolvedItems.map(item => item.key)
    )
    const lastStackItem = this.state.stack[this.state.stack.length - 1]
    const leftResolvedItem = resolvedItems[0]

    const transitionOptions = { ...DEFAULT_TRANSITION, ...pick(lastStackItem, TRANSITION_KEYS) }

    this.setState({ stack: nextStack, mounted: nextMounted, transitionOptions }, () => {
      window.scrollTo(0, leftResolvedItem.scrollTop)

      resolvedItems.map(item => item.resolve())
    })
  }

  pop = () => {
    return new Promise(resolve => {
      if (this.state.stack.length === 0) {
        resolve()
        return
      }
      clearTimeout(this._mountTimeout)

      const lastStackItem = this.state.stack[this.state.stack.length - 1]
      const mounted = omit(this.state.mounted, lastStackItem.key)
      const transitionOptions = { ...DEFAULT_TRANSITION, ...pick(lastStackItem, TRANSITION_KEYS) }

      const stack = this.state.stack.slice(0, -1)
      this.setState({ stack, transitionOptions, mounted }, () => {
        window.scrollTo(0, lastStackItem.scrollTop)
        lastStackItem.resolve()
        resolve()
      })
    })
  }

  next = () => {
    if (this.state.stack.length > 0) {
      const lastStackWithNext = findLast(this.state.stack, item => 'next' in item)
      if (lastStackWithNext) {
        return lastStackWithNext.next
      }
    }
  }

  _didMount = (stack = this.state.stack) => {
    const lastStackItem = stack[stack.length - 1]
    const mounted = { ...this.state.mounted, [lastStackItem.key]: true }
    this.setState({ mounted }, () => {
      const style = window.getComputedStyle(this._mobile.current, null)
      if (style['display'] !== 'none') {
        window.scrollTo(0, 0)
      }
    })
  }
}
